import { useCallback } from 'react'
import { useMotionValue, useSpring } from 'framer-motion'
import type { MotionValue } from 'framer-motion'

type PointerParallax = {
  /** Normalised pointer position, -1 → 1, spring-smoothed. */
  x: MotionValue<number>
  y: MotionValue<number>
  onPointerMove: (event: React.PointerEvent) => void
  onPointerLeave: () => void
}

const pointerSpring = { stiffness: 55, damping: 18, mass: 0.7, restDelta: 0.001 }

/**
 * Very light pointer parallax for desktop pointers only. Values stay normalised
 * so callers decide how many pixels/degrees they translate into.
 */
export function usePointerParallax(enabled: boolean): PointerParallax {
  const rawX = useMotionValue(0)
  const rawY = useMotionValue(0)
  const x = useSpring(rawX, pointerSpring)
  const y = useSpring(rawY, pointerSpring)

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!enabled || event.pointerType !== 'mouse') return
      rawX.set((event.clientX / window.innerWidth - 0.5) * 2)
      rawY.set((event.clientY / window.innerHeight - 0.5) * 2)
    },
    [enabled, rawX, rawY],
  )

  const onPointerLeave = useCallback(() => {
    rawX.set(0)
    rawY.set(0)
  }, [rawX, rawY])

  return { x, y, onPointerMove, onPointerLeave }
}
